import { Accordion, Checkbox, Divider, Group, Paper, ScrollArea, Stack, Text } from "@mantine/core";
import cx from "clsx";
import { type FC, Fragment, useEffect, useState } from "react";

import { PurpleIndicator } from "@/sales-tools/micro-learning/components/icons";

import type { EvaluationDetailsBody } from "../api/evaluations/types";

import { CHECKLIST_CATEGORIES } from "../utils";
import classes from "./EvaluationPage.module.css";

export interface SalesBehaviorsChecklistProps {
    activeBehaviorCategory?: string;
    activeBehaviorIndex: number;
    behaviors: EvaluationDetailsBody[];
    onClickBehavior?: (index: number) => void;
}

export interface SalesBehaviorsChecklistItemProps {
    active: boolean;
    behavior: string;
    behaviorHasDetails?: boolean;
    checked: boolean;
    itemNumber: number;
    onClick?: () => void;
}

export const SalesBehaviorsChecklistHeading: FC<{ children: React.ReactNode }> = ({ children }) => {
    return (
        <Stack gap="0.25rem" px="xs">
            <Text fw={700} size="md" c="var(--mantine-color-gray-0)">
                {children}
            </Text>
        </Stack>
    );
};

export const SalesBehaviorsChecklistItem: FC<SalesBehaviorsChecklistItemProps> = ({
    active,
    behavior,
    behaviorHasDetails,
    checked,
    itemNumber,
    onClick,
}) => {
    return (
        <Group
            p="0.625rem"
            gap="0.75rem"
            className={cx(classes.behaviorChecklistItem, {
                [classes.activeChecklistItem]: active,
                [classes.behaviorDisabled]: !behaviorHasDetails,
            })}
            onClick={onClick}
        >
            <Text size="0.875rem" fw="500" c="var(--mantine-color-gray-0)">
                {itemNumber}.
            </Text>
            <Text
                size="0.875rem"
                flex="1"
                fw="500"
                c="var(--mantine-color-gray-0)"
                className={classes.checklistContent}
            >
                {behavior}
            </Text>
            <Checkbox checked={checked} readOnly={true} size="xs" />
        </Group>
    );
};

// Left hand side: collapsible checklist of sales behaviors
//   - can we add score to top of category?
// Right hand side: Add category header to behavior breakdown

export const SalesBehaviorsChecklist: FC<SalesBehaviorsChecklistProps> = ({
    activeBehaviorCategory,
    activeBehaviorIndex,
    behaviors,
    onClickBehavior,
}) => {
    const [activeCategory, setActiveCategory] = useState<string | null>(CHECKLIST_CATEGORIES[0].category);

    useEffect(() => {
        if (activeBehaviorCategory) {
            setActiveCategory(activeBehaviorCategory);
        }
    }, [activeBehaviorCategory, activeBehaviorIndex]);

    return (
        <Paper
            display="flex"
            shadow="md"
            radius="md"
            p="1rem"
            w="100%"
            bg="var(--mantine-color-dark-5)"
            bdrs="0.75rem"
            flex="1"
            style={{
                flexDirection: "column",
                maxHeight: "100%",
            }}
        >
            <ScrollArea offsetScrollbars scrollbarSize={8} type="hover" flex="1">
                <Stack gap="md" h="100%">
                    <Accordion
                        chevronPosition="right"
                        value={activeCategory}
                        onChange={setActiveCategory}
                        styles={{
                            chevron: {
                                color: "var(--mantine-color-success-4)",
                                scale: 1.2,
                            },
                            content: {
                                padding: 0,
                            },
                            control: {
                                color: "var(--mantine-color-dark-2)",
                                padding: 0,
                                paddingRight: "0.25rem",
                            },
                        }}
                    >
                        {/* Loop through each category of behaviors */}
                        {CHECKLIST_CATEGORIES.map((category, index) => {
                            let salesBehaviorIterator = 0;

                            return (
                                <Accordion.Item value={category.category} key={category.category}>
                                    <Accordion.Control>
                                        <SalesBehaviorsChecklistHeading>
                                            {category.category}
                                        </SalesBehaviorsChecklistHeading>
                                    </Accordion.Control>
                                    <Accordion.Panel>
                                        <Stack gap={0} mb={index === CHECKLIST_CATEGORIES.length - 1 ? "0" : "xl"}>
                                            {/* Loop through each behavior within a given category */}
                                            {category.behaviors.map((behavior, i) => {
                                                const behaviorItem = behaviors.find((b) =>
                                                    b.behavior
                                                        ? behavior.evaluationBehavior
                                                              .toLocaleLowerCase()
                                                              .includes(b.behavior.toLocaleLowerCase())
                                                        : false,
                                                );
                                                const behaviorIndex = behaviors.findIndex((b) =>
                                                    b.behavior
                                                        ? behavior.evaluationBehavior
                                                              .toLocaleLowerCase()
                                                              .includes(b.behavior.toLocaleLowerCase())
                                                        : false,
                                                );

                                                return (
                                                    <Fragment key={behavior.evaluationBehavior}>
                                                        <Divider />
                                                        <SalesBehaviorsChecklistItem
                                                            active={activeBehaviorIndex === behaviorIndex}
                                                            behavior={behavior.displayText}
                                                            behaviorHasDetails={behaviorIndex >= 0}
                                                            checked={!!behaviorItem?.behaviorPresent}
                                                            itemNumber={i + 1}
                                                            onClick={() => {
                                                                if (behaviorIndex >= 0) {
                                                                    onClickBehavior?.(behaviorIndex);
                                                                }
                                                            }}
                                                        />
                                                    </Fragment>
                                                );
                                            })}
                                            {/* Loop through behaviors that only show if an offer was detected, and add them to the checklist */}
                                            {category.saleDetectedBehaviors.map((salesBehavior) => {
                                                const salesBehaviorItem = behaviors.find((b) =>
                                                    b.behavior
                                                        ? salesBehavior.evaluationBehavior
                                                              .toLocaleLowerCase()
                                                              .includes(b.behavior.toLocaleLowerCase())
                                                        : false,
                                                );
                                                const salesBehaviorIndex = behaviors.findIndex((b) =>
                                                    b.behavior
                                                        ? salesBehavior.evaluationBehavior
                                                              .toLocaleLowerCase()
                                                              .includes(b.behavior.toLocaleLowerCase())
                                                        : false,
                                                );
                                                // In order to ensure the list number is correct, we only iterate the iterator if the behaviorItem is found
                                                // This is to ensure that the numbering is correct even if some behaviors are not present
                                                salesBehaviorIterator = salesBehaviorItem
                                                    ? salesBehaviorIterator + 1
                                                    : salesBehaviorIterator;

                                                return salesBehaviorItem ? (
                                                    <Fragment key={salesBehavior.evaluationBehavior}>
                                                        <Divider />
                                                        <SalesBehaviorsChecklistItem
                                                            active={activeBehaviorIndex === salesBehaviorIndex}
                                                            behavior={salesBehavior.displayText}
                                                            behaviorHasDetails={salesBehaviorIndex >= 0}
                                                            checked={!!salesBehaviorItem?.behaviorPresent}
                                                            itemNumber={
                                                                category.behaviors.length + salesBehaviorIterator
                                                            }
                                                            onClick={() => {
                                                                if (salesBehaviorIndex >= 0) {
                                                                    onClickBehavior?.(salesBehaviorIndex);
                                                                }
                                                            }}
                                                        />
                                                    </Fragment>
                                                ) : null;
                                            })}
                                        </Stack>
                                    </Accordion.Panel>
                                </Accordion.Item>
                            );
                        })}
                    </Accordion>
                </Stack>
            </ScrollArea>
            <Group gap="0.5rem" mt="1rem" ml="0.25rem">
                <PurpleIndicator fill="var(--mantine-color-asurion-3)" style={{ width: "8px", height: "8px" }} />
                <Text size="0.75rem" c="var(--mantine-color-gray-0)" fs="italic">
                    Checklists are dynamic
                </Text>
            </Group>
        </Paper>
    );
};

export default SalesBehaviorsChecklist;
